import { useMemo, useState, useEffect } from 'react';
import { PagingQuery } from 'tea-component';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import useStoreRankMoreCoreLineChart from '@/pages/intelligence/pages/StoreRanks/components/StoreRankMoreCore/hooks/useStoreRankMoreCoreLineChart';
import { fetchStoreRankMoreRanks, fetchStoreRankMoreTrends } from '../services';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { TOPGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { handleTableDefaultSelectGames } from '@/pages/intelligence/utils/tableUtils';
import {
  analysisPlatformTopInfo,
  getStoreRankPlatformName,
  getStoreRankTopName,
} from '@/pages/intelligence/_config/storeRank/utils';

import type { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import type {
  TableSelectionType,
  TableSelectStatusType,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import type { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import type {
  IStoreRankMoreCoreRankPrimaryKey,
  IStoreRankMoreCoreFetchParams,
  IStoreRankMoreCoreRank,
  IStoreRankMoreCoreTrend,
} from '@/pages/intelligence/types/storeRank';

const getOrderMetricName = (
  req: any,
  cfg: { entityType: any; isSectional: boolean; translater: any; },
): string => {
  if (!req) return '';
  const { entityType, isSectional, translater } = cfg ?? {};
  const orderMetric = req?.order_metric;
  if (isSectional) return orderMetric ? `#${orderMetric}` : '';
  const orderSource = req?.order_source;
  const { platform, top } = analysisPlatformTopInfo(orderSource);
  const platformName = getStoreRankPlatformName(entityType, platform);
  const topName = getStoreRankTopName(entityType, top, translater);
  return entityType !== 'mobile' && platformName ? `${platformName} ${topName}` : topName;
};

const getIsLineVisualisNoData = (
  data: { [key: string]: IStoreRankMoreCoreTrend[]; },
  segment: string,
): boolean => {
  const segmentData = data?.[segment];
  const flag1 = segmentData?.length > 0;
  const flag2 = segmentData?.some((item) => item?.history?.length > 0);
  return !(flag1 && flag2);
};

export default function useStoreRankMoreCore(props: {
  entityType: 'mobile' | 'pc' | 'console';
  pageLogCfg?: any;
  primaryKey: IStoreRankMoreCoreRankPrimaryKey;
  platformTopMetrics: string[];
  initialFetchParams: IStoreRankMoreCoreFetchParams;
  getIsSectional: (cfg: IStoreRankMoreCoreFetchParams) => boolean;
}) {
  const {
    entityType,
    pageLogCfg,
    primaryKey,
    platformTopMetrics,
    initialFetchParams,
    getIsSectional,
  } = props;
  const { locale, formatMessage } = useIntlExtend();

  const [fetchParams, setFetchParams] = useState<IStoreRankMoreCoreFetchParams>(initialFetchParams);

  const [isVisualisLoading, setIsVisualisLoading] = useState(true);
  const [sortKeyName, setSortKeyName] = useState<any>('');

  const [isTableLoading, setIsTableLoading] = useState(true);
  const [tableDimensionsData, setTableDimensionsData] = useState<string[]>([]);
  const [tableData, setTableData] = useState<IStoreRankMoreCoreRank[]>([]);
  const [tableTotal, setTableTotal] = useState(0);

  // 柱状图数据
  const [barOrPieData, setBarOrPieData] = useState<IStoreRankMoreCoreRank[]>([]);
  const [lineData, setLineData] = useState<{ [key: string]: IStoreRankMoreCoreTrend[]; }>({});

  // 宽表勾选的数据
  // 是否重新勾选过游戏
  const [operatedGame, setOperatedGame] = useState(false);
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);
  const [tableDimensionsForClearSearch, setTableDimensionsForClearSearch] = useState<string[]>([]);
  const [tableListForClearSearch, setTableListForClearSearch] = useState<IStoreRankMoreCoreRank[]>(
    [],
  );
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);

  // 折线图相关hooks
  const {
    seriesData,
    xAxisData,
    yAxisFormatter,
    // @ts-ignore
    tooltipFormatter,
    createLegendsCustomMade,
    currentLine,
    segments,
    setSegment,
    segment,
  } = useStoreRankMoreCoreLineChart({
    entityType,
    topChartName: fetchParams?.topchart_name,
    dateType: fetchParams?.granularity,
    platformTopMetrics,
    data: lineData,
  });

  const isSectional = getIsSectional(fetchParams);

  const isLineVisualisNoData = useMemo(
    () => getIsLineVisualisNoData(lineData, segment),
    [lineData, segment],
  );

  const { platform, top } = useMemo(
    () => analysisPlatformTopInfo(fetchParams?.topchart_name),
    [fetchParams?.topchart_name],
  );

  const { platformName, topName } = useMemo(() => {
    const platformName = getStoreRankPlatformName(entityType, platform);
    const topNameLocaleKey = getStoreRankTopName(entityType, top, formatMessage);
    const topName = formatMessage({ id: topNameLocaleKey });
    return { platformName, topName };
  }, [locale, platform, top]);

  const getGameData = async (params: IStoreRankMoreCoreFetchParams, isCheckOrSearch: boolean) => {
    try {
      setSortKeyName('');
      setTableDimensionsData([]);
      setTableData([]);
      setTableTotal(0);
      setFetchParams(params);
      const isSectionalTmp = params?.regions?.length > 1;
      const res = await fetchStoreRankMoreRanks(params);
      const resData = res?.data ?? {};
      const extData = res?.ext ?? {};
      const metricList = resData?.metric_list ?? [];
      const list = resData?.list ?? [];
      const total = resData?.total ?? list?.length; // 搜索不返回total用表格长度计算

      if (params?.page === 1 && !isCheckOrSearch) {
        /** 默认选择第一页的前6作为表格全选展示 */
        const defaultGames = handleTableDefaultSelectGames(list?.slice(0, TOPGAMENUM), primaryKey);
        setDefaultSelectionGames(defaultGames);
        /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
        if (!isCheckOrSearch) {
          setTableSelection(defaultGames);
          const barOrPieDataTmp = handleTableToBar(list);
          setBarOrPieData(barOrPieDataTmp?.slice(0, TOPGAMENUM));
        }
      }
      if (!params?.search_ids?.length) {
        // 有game_code表示是搜索，则不存页面数据
        setTableDimensionsForClearSearch(metricList);
        setTableListForClearSearch(list);
        setTotalForClearSearch(total);
      }

      // 如果 extData.entity_info 存在数据，则添加到 list.entity_info
      const entityInfoMap = extData?.entity_info || {};
      const listWithEntityInfo = list.map((item: any) => ({
        ...item,
        entity_info: entityInfoMap[item?.unified_id || item?.ed_id] || null,
      }));
      setTableDimensionsData(metricList);
      setTableData(listWithEntityInfo);
      setTableTotal(total);

      /** 时间及排序参数回填 */
      const req = res?.req ?? {};
      const fetchParamsTmp = {
        ...fetchParams,
        ...params,
        order_source: req?.order_source,
        order_metric: req?.order_metric,
        order: req?.order,
        ratio_type: req?.ratio_type as 0 | 1,
        granularity: req?.granularity,
        start_date: req?.start_date ?? params?.start_date,
        end_date: req?.end_date ?? params?.end_date,
      };
      const sortKeyNameTmp = getOrderMetricName(fetchParamsTmp, {
        entityType,
        isSectional: isSectionalTmp,
        translater: formatMessage,
      });
      setFetchParams(fetchParamsTmp);
      setSortKeyName(sortKeyNameTmp);

      return res;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    const sortKeyNameTmp = getOrderMetricName(fetchParams, {
      entityType,
      isSectional,
      translater: formatMessage,
    });
    setSortKeyName(sortKeyNameTmp);
  }, [locale, entityType, isSectional]);

  const getLineChartData = async (params: IStoreRankMoreCoreFetchParams) => {
    try {
      setLineData({});
      const res = await fetchStoreRankMoreTrends(params);
      const lineDataRes = res?.data?.time_list ?? {};
      setLineData(lineDataRes);
    } catch (e) {
      console.error(e);
    } finally {
    }
  };

  const handleTableToBar = (list: any[], end = TOPGAMENUM) => {
    if (!Array.isArray(list)) {
      return [];
    }
    return list.slice(0, end);
  };

  const handleClearSearch = () => {
    setFetchParams({ ...fetchParams, search_ids: [] });
    setTableDimensionsData(tableDimensionsForClearSearch);
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
  };

  const transTableDataForSearch = (res: any) => {
    const resultList = res?.data?.list;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    // 如果是移动端则使用unified_id，否则使用primaryKey
    const idKey = entityType === 'mobile' ? 'unified_id' : primaryKey;
    const tranksList: RecordItemType[] = resultList.map((item) => ({
      id: item?.[idKey],
      imgSrc: item?.cover,
      lineOneStr: item?.name,
      lineTwoImg: getCountryFlag(item?.publisher_addr),
      lineTwoImgName: item?.publisher_addr,
      lineTwoStr: item?.publisher,
    }));
    return tranksList;
  };

  const handleConfirm = (payload: any) => {
    const fetchParamsTmp = {
      ...fetchParams,
      ...payload,
      region_type: 'market',
      order_source: '',
      order_metric: 'rank',
      order: 'desc',
      ratio_type: 0,
      page: 1,
      page_size: 100,
    };
    // 请求接口
    setIsTableLoading(true);
    setIsVisualisLoading(true);
    setOperatedGame(false);
    getData(fetchParamsTmp, false);
    getChartData(fetchParamsTmp, true);
  };

  const handleReset = () => {};

  const handleInit = () => {};

  const getData = (
    params: IStoreRankMoreCoreFetchParams,
    isCheckGame: boolean,
    isSearch = false,
  ) => {
    getGameData(
      {
        ...params,
        search_ids: isSearch ? params?.search_ids : [],
        regions: /* checkNintendoUsOnly(params?.topchart_name) ? ['us'] : */ params?.regions,
      },
      isCheckGame,
    )
      .then(() => {
        setIsTableLoading(false);
      })
      .catch(() => {
        setIsTableLoading(false);
      });
  };

  const getChartData = (params: IStoreRankMoreCoreFetchParams, isNewFetch = false) => {
    const isSectionalTmp = params?.regions?.length > 1;
    if (isSectionalTmp) return;
    setIsVisualisLoading(true);
    getLineChartData({
      ...params,
      search_ids: isNewFetch ? [] : params?.search_ids,
      page: 1,
      page_size: 6,
      regions: /* checkNintendoUsOnly(params?.topchart_name) ? ['us'] : */ params?.regions,
    })
      .then(() => {
        setIsVisualisLoading(false);
      })
      .catch(() => {
        setIsVisualisLoading(false);
      });
  };

  const getTableSearchList = (id: string) => {
    console.log('getTableSearchList - 2', id);
    setIsTableLoading(true);
    getData({ ...fetchParams, search_ids: [id] }, true, true);
  };

  const onTableSortChage = (e: SortByItemType) => {
    const fetchParamsTmp: IStoreRankMoreCoreFetchParams = {
      ...fetchParams,
      order_source: e?.orderSource ?? '',
      order_metric: e?.sortMetric ?? '',
      order: e?.sortVal ?? 'desc',
      ratio_type: e?.ratioType ?? 0,
      page: 1,
      page_size: 100,
    };
    // setSortKey(e?.sortVal);
    setIsTableLoading(true);
    if (!operatedGame) {
      /** 没有勾选时点排序则重新Loading柱状图数据 */
      setIsVisualisLoading(true);
      getChartData(fetchParamsTmp);
    }
    getData(fetchParamsTmp, operatedGame);
  };

  const onTablePagingChange = (e: PagingQuery) => {
    const fetchParamsTmp: IStoreRankMoreCoreFetchParams = {
      ...fetchParams,
      page: e?.pageIndex ?? 1,
      page_size: e?.pageSize ?? 100,
    };
    setIsTableLoading(true);
    getData(fetchParamsTmp, operatedGame);
  };

  // 表格列表项选择事件回调
  const onTableCheckGame = (
    selectList: TableSelectionType[],
    checkStatus: TableSelectStatusType,
  ) => {
    // setIsNewCheckGame(true);
    const idList = selectList?.map((item) => item?.[primaryKey] || '') ?? [];
    const packageIdList = selectList?.map((item) => item?.package_id || '') ?? [];
    const fetchParamsTmp = {
      ...fetchParams,
      page: 1,
      page_size: 6,
      search_ids: [...idList],
      package_ids: [...packageIdList],
    };
    getChartData(fetchParamsTmp);
    // /**是否勾选了游戏 */
    if (checkStatus === 'cancel' || checkStatus === 'all' || selectList.length === 0) {
      setOperatedGame(false);
    } else {
      setOperatedGame(true);
    }
    /** 将表格数据转换为柱状或饼 */
    // setBarOrPieData(selectList);
    setTableSelection(selectList);
    /** 处理折线图数据*/
  };

  const handlePageInit = (payload: Partial<IStoreRankMoreCoreFetchParams>) => {
    const fetchParamsTmp = {
      ...fetchParams,
      ...payload,
    };
    setIsTableLoading(true);
    setIsVisualisLoading(true);
    getData(fetchParamsTmp, operatedGame);
    getChartData(fetchParamsTmp);
  };

  useEffect(() => {
    if (pageLogCfg) {
      /** 页面打点 */
      buriedPointPage(pageLogCfg);
    }
  }, []);

  return {
    fetchParams,
    isSectional,
    platform,
    platformName,
    top,
    topName,

    handleInit,
    handleConfirm,
    handleReset,

    isVisualisLoading,
    isVisualisNoData: !isVisualisLoading && isLineVisualisNoData,
    barOrPieData,
    seriesData,
    xAxisData,
    sortKeyName,
    currentLine,
    segments,
    segment,
    setSegment,
    yAxisFormatter,
    tooltipFormatter,
    createLegendsCustomMade,

    isTableLoading,
    isTableNoData: !isTableLoading && !tableData?.length,
    tableDimensionsData,
    tableTotal,
    tableData,
    tableSelection,
    defaultSelectionGames,
    transTableDataForSearch,
    getTableSearchList,
    handleClearSearch,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    onPageInit: handlePageInit,
  };
}
